import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from '@/lib/api';
import { AuthContext, type AuthState, TOKEN_STORAGE_KEY } from '@/lib/auth-store';

/**
 * Hydrates auth state on mount: if a token sits in localStorage, ask the server
 * "who am I?". Anything other than success → fall back to anonymous. We never
 * surface the raw token to UI; only `signIn`/`signOut` actions touch it.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const hydrate = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      setState({ status: 'anonymous' });
      return;
    }
    try {
      const me = await api.auth.me(token);
      setState({ status: 'authenticated', profile: me.profile, token });
    } catch (e) {
      // Stale or revoked token. Wipe and become anonymous.
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      }
      setState({ status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await api.auth.signIn({ email, password });
    localStorage.setItem(TOKEN_STORAGE_KEY, res.token);
    setState({ status: 'authenticated', profile: res.profile, token: res.token });
  }, []);

  const signOut = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    try {
      await api.auth.signOut(token);
    } catch {
      // Even if the server call fails (offline, etc.), local sign-out must succeed.
    }
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setState({ status: 'anonymous' });
  }, []);

  const value = useMemo(
    () => ({ state, signIn, signOut, refresh: hydrate }),
    [state, signIn, signOut, hydrate],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
